// src/components/modal/MenuItemDetails/utils/priceUtils.ts
import type { MenuItem } from "~/types/new_types";
import type { ItemCard } from "../types";

/**
 * Calculates the base price for an item based on selected variants
 * For combo/upgrade variants, the base price is replaced
 */
export function calculateBasePrice(
  item: MenuItem,
  itemCards: ItemCard[],
  cardIndex?: number
): number {
  let basePrice = item.discounted_price || item.price;

  const cardsToCheck =
    cardIndex !== undefined ? [itemCards[cardIndex]] : itemCards;

  for (const card of cardsToCheck) {
    if (!card || !card.variants) continue;

    // Check variants_and_options for combo/upgrade pricing (replace base price)
    if (item.variants_and_options && Array.isArray(item.variants_and_options)) {
      for (const group of item.variants_and_options) {
        const selection = card.variants[group.id];
        const groupData = group as any;

        if (
          selection &&
          (groupData.action_type?.toLowerCase() === "upgrade" ||
            groupData.isComboUpgrade)
        ) {
          let upgradePrice;
          if (
            typeof selection === "object" &&
            (selection as any).price !== undefined
          ) {
            upgradePrice = (selection as any).price;
          } else if (
            typeof selection === "string" &&
            Array.isArray(groupData.data)
          ) {
            const selectedOption = groupData.data.find(
              (opt: any) => opt.name === selection
            );
            if (selectedOption?.price !== undefined) {
              upgradePrice = selectedOption.price;
            }
          } else if (
            Array.isArray(groupData.data) &&
            groupData.data.length === 1 &&
            groupData.data[0].price !== undefined
          ) {
            upgradePrice = groupData.data[0].price;
          }

          if (upgradePrice !== undefined) {
            basePrice = Number(upgradePrice);
            break;
          }
        }
      }
    }

    // Check meals_and_combos for combo pricing
    if (item.meals_and_combos && Array.isArray(item.meals_and_combos)) {
      for (const combo of item.meals_and_combos) {
        const selectedCombo = card.variants[combo.id];
        if (selectedCombo && combo.data) {
          let comboPrice;
          if (
            typeof selectedCombo === "object" &&
            (selectedCombo as any).price !== undefined
          ) {
            comboPrice = (selectedCombo as any).price;
          } else if ((combo.data as any).combo_price !== undefined) {
            comboPrice = (combo.data as any).combo_price;
          }

          if (comboPrice !== undefined) {
            basePrice = Number(comboPrice);
            break;
          }
        }
      }
    }
  }

  return basePrice;
}

/**
 * Calculates the total price for all items including add-ons
 */
export function calculateTotalPrice(
  item: MenuItem,
  itemCards: ItemCard[],
  hasVariants: boolean,
  hasUserInteracted: boolean,
  nonVariantQuantity: number,
  currentQuantity: number
): number {
  let totalPrice = 0;

  if (
    item.variants_and_options &&
    item.variants_and_options.length > 0 &&
    itemCards.length > 0
  ) {
    // For items with variants, calculate price per card
    for (const card of itemCards) {
      let cardPrice = item.discounted_price || item.price;
      let addOnPrice = 0;

      // Check variants_and_options for pricing
      if (item.variants_and_options && Array.isArray(item.variants_and_options)) {
        for (const group of item.variants_and_options) {
          const selection = card.variants[group.id];
          if (!selection) continue;

          const groupData = group as any;
          const actionType = groupData.action_type?.toLowerCase();
          const isUpgrade = actionType === "upgrade" || groupData.isComboUpgrade;

          if (isUpgrade) {
            // Upgrade variants replace the base price
            let upgradePrice;
            if (
              typeof selection === "object" &&
              (selection as any).price !== undefined
            ) {
              upgradePrice = (selection as any).price;
            } else if (Array.isArray(groupData.data)) {
              if (typeof selection === "string") {
                const selectedOption = groupData.data.find(
                  (opt: any) => opt.name === selection
                );
                if (selectedOption?.price) {
                  upgradePrice = selectedOption.price;
                }
              } else if (
                groupData.data.length === 1 &&
                groupData.data[0].price !== undefined
              ) {
                upgradePrice = groupData.data[0].price;
              }
            }

            if (upgradePrice !== undefined) {
              cardPrice = Number(upgradePrice);
            }
          } else {
            // Selection/add-on variants add to the price
            if (Array.isArray(groupData.data)) {
              if (typeof selection === "string") {
                const selectedOption = groupData.data.find(
                  (opt: any) => opt.name === selection
                );
                if (selectedOption?.price) {
                  addOnPrice += Number(selectedOption.price);
                }
              } else if (Array.isArray(selection)) {
                for (const sel of selection) {
                  const selectedOption = groupData.data.find(
                    (opt: any) => opt.name === sel
                  );
                  if (selectedOption?.price) {
                    addOnPrice += Number(selectedOption.price);
                  }
                }
              }
            }
          }
        }
      }

      // Check meals_and_combos for combo pricing
      if (item.meals_and_combos && Array.isArray(item.meals_and_combos)) {
        for (const combo of item.meals_and_combos) {
          const selectedCombo = card.variants[combo.id];
          if (selectedCombo && combo.data) {
            let comboPrice;
            if (
              typeof selectedCombo === "object" &&
              (selectedCombo as any).price !== undefined
            ) {
              comboPrice = (selectedCombo as any).price;
            } else if ((combo.data as any).combo_price !== undefined) {
              comboPrice = (combo.data as any).combo_price;
            }

            if (comboPrice !== undefined) {
              // For meals/combos, replace the base price
              cardPrice = Number(comboPrice);
            }
          }
        }
      }

      totalPrice += (cardPrice + addOnPrice) * card.quantity;
    }
  } else if (hasVariants) {
    const quantity = currentQuantity > 0 ? currentQuantity : 1;
    totalPrice = (item.discounted_price || item.price) * quantity;
  } else {
    const quantity = Math.max(
      hasUserInteracted ? nonVariantQuantity : currentQuantity,
      0
    );
    totalPrice = (item.discounted_price || item.price) * quantity;
  }

  return totalPrice;
}

/**
 * Calculates the add-on total for a specific card
 */
export function calculateCardAddOnTotal(
  item: MenuItem,
  card: ItemCard
): number {
  let addOnTotal = 0;

  if (!item.variants_and_options || !Array.isArray(item.variants_and_options)) {
    return 0;
  }

  for (const group of item.variants_and_options) {
    const selection = card.variants[group.id];
    if (!selection) continue;

    const groupData = group as any;
    const actionType = groupData.action_type?.toLowerCase();
    const isUpgrade = actionType === "upgrade" || groupData.isComboUpgrade;

    // Only count add-ons, not upgrades (which replace base price)
    if (!isUpgrade && Array.isArray(groupData.data)) {
      if (typeof selection === "string") {
        const selectedOption = groupData.data.find(
          (opt: any) => opt.name === selection
        );
        if (selectedOption?.price) {
          addOnTotal += Number(selectedOption.price);
        }
      } else if (Array.isArray(selection)) {
        for (const sel of selection) {
          const selectedOption = groupData.data.find(
            (opt: any) => opt.name === sel
          );
          if (selectedOption?.price) {
            addOnTotal += Number(selectedOption.price);
          }
        }
      }
    }
  }

  return addOnTotal;
}
